import type { Root as SelectPrimitiveRoot } from '@radix-ui/react-select';
import type { ComponentProps } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { FormErrorMessage } from '@/components/form/form-error-message';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type SelectControlledProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  options: { label: string; value: string }[];
  error?: string;
  isLoading?: boolean;
  placeholder?: string;
  label?: string;
} & ComponentProps<typeof SelectPrimitiveRoot>;

export function SelectControlled<T extends FieldValues>({
  control,
  name,
  options,
  error,
  isLoading,
  placeholder,
  label,
  ...props
}: SelectControlledProps<T>) {
  return (
    <div className='space-y-2'>
      <Label htmlFor={name}>{label}</Label>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <>
            <Select
              disabled={isLoading}
              onValueChange={field.onChange}
              value={field.value}
              {...props}
            >
              <SelectTrigger className='w-full' id={name}>
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
              <SelectContent>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormErrorMessage error={error} />
          </>
        )}
      />
    </div>
  );
}
